/**
 * Machine Dashboard Page
 * Displays machine capacity groups, machine status summary,
 * and machine operations with filtering.
 */

import { useState } from 'react';
import { Layout } from '../components/layout';
import {
  useMachineCapacityGroups,
  useMachines,
  useMachineStatusSummary,
  useMachineOperations,
  getMachineStatusColor,
  getMachineStatusLabel,
} from '../hooks';
import type {
  MachineCapacityGroupListItem,
  MachineNumberListItem,
  MachineOperationListItem,
  MachineStatus,
} from '../types';
import {
  Cog,
  Server,
  Activity,
  AlertTriangle,
  CheckCircle2,
  PauseCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Search,
  ChevronRight,
  Settings2,
  Wrench,
} from 'lucide-react';
import clsx from 'clsx';

// ============ Status Icon Helper ============

function StatusIcon({ status }: { status: MachineStatus }) {
  switch (status) {
    case 'available':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'in_use':
      return <Activity className="w-4 h-4 text-blue-500" />;
    case 'maintenance':
      return <Wrench className="w-4 h-4 text-amber-500" />;
    case 'offline':
      return <XCircle className="w-4 h-4 text-red-500" />;
    default:
      return <Activity className="w-4 h-4 text-gray-400" />;
  }
}

// ============ Status Summary Cards ============

/** Parse the status summary API response into a flat map */
function parseStatusSummary(data: any): Record<string, number> {
  const map: Record<string, number> = {
    available: 0,
    in_use: 0,
    maintenance: 0,
    offline: 0,
  };
  if (data?.by_status) {
    for (const entry of data.by_status) {
      map[entry.status] = entry.count;
    }
  }
  return map;
}

function StatusSummary({
  data,
  loading,
}: {
  data: any;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const counts = parseStatusSummary(data);

  const cards = [
    {
      label: 'Available',
      value: counts.available,
      color: 'text-green-600',
      bg: 'bg-green-50 border-green-200',
      icon: CheckCircle2,
    },
    {
      label: 'In Use',
      value: counts.in_use,
      color: 'text-blue-600',
      bg: 'bg-blue-50 border-blue-200',
      icon: Activity,
    },
    {
      label: 'Maintenance',
      value: counts.maintenance,
      color: 'text-amber-600',
      bg: 'bg-amber-50 border-amber-200',
      icon: Wrench,
    },
    {
      label: 'Offline',
      value: counts.offline,
      color: 'text-red-600',
      bg: 'bg-red-50 border-red-200',
      icon: XCircle,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className={clsx(
            'border rounded-lg p-4 flex items-center gap-3',
            card.bg
          )}
        >
          <card.icon className={clsx('w-8 h-8', card.color)} />
          <div>
            <p className={clsx('text-2xl font-bold', card.color)}>
              {card.value}
            </p>
            <p className="text-xs text-gray-500">{card.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Capacity Group Card ============

function CapacityGroupCard({
  group,
  selected,
  onClick,
}: {
  group: MachineCapacityGroupListItem;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left border rounded-lg p-4 transition-all',
        selected
          ? 'border-[#45bdd6] bg-[#45bdd6]/5 shadow-sm'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
      )}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-800 truncate">
            {group.erp_code || group.id}
          </p>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {group.name || 'Capacity Group'}
          </p>
        </div>
        <ChevronRight
          className={clsx(
            'w-4 h-4 flex-shrink-0 transition-colors',
            selected ? 'text-[#45bdd6]' : 'text-gray-300'
          )}
        />
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span
          className={clsx(
            'text-xs px-2 py-0.5 rounded-full font-medium',
            group.is_active
              ? 'bg-green-50 text-green-600'
              : 'bg-gray-100 text-gray-400'
          )}
        >
          {group.is_active ? 'Active' : 'Inactive'}
        </span>
        <span className="text-xs text-gray-400">
          {group.capacity_type || '—'}
        </span>
      </div>
    </button>
  );
}

// ============ Machine Row ============

function MachineRow({ machine }: { machine: MachineNumberListItem }) {
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <StatusIcon status={machine.status as MachineStatus} />
          <span className="text-sm font-medium text-gray-800">
            {machine.erp_code || machine.id}
          </span>
        </div>
      </td>
      <td className="py-3 px-4 text-sm text-gray-600">
        {machine.name || '—'}
      </td>
      <td className="py-3 px-4">
        <span
          className={clsx(
            'text-xs px-2 py-1 rounded-full font-medium',
            getMachineStatusColor(machine.status as MachineStatus)
          )}
        >
          {getMachineStatusLabel(machine.status as MachineStatus)}
        </span>
      </td>
      <td className="py-3 px-4 text-sm text-gray-500 text-right">
        {machine.work_center_code || '—'}
      </td>
    </tr>
  );
}

// ============ Main Page ============

export function MachineDashboardPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Data queries
  const capacityGroups = useMachineCapacityGroups(
    searchQuery ? { search: searchQuery } : undefined
  );
  const machines = useMachines(
    selectedGroupId ? { capacity_group: selectedGroupId } : undefined
  );
  const statusSummary = useMachineStatusSummary();
  const operations = useMachineOperations(
    selectedGroupId ? { capacity_group: selectedGroupId } : undefined
  );

  const groupsList = capacityGroups.data ?? [];
  const machinesList = machines.data ?? [];

  return (
    <Layout>
      <div className="p-6 space-y-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Settings2 className="w-7 h-7 text-[#45bdd6]" />
              Machine Dashboard
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Capacity groups, machine status and operations
            </p>
          </div>
          <button
            onClick={() => {
              capacityGroups.refetch();
              machines.refetch();
              statusSummary.refetch();
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Status Summary */}
        <StatusSummary
          data={statusSummary.data ?? null}
          loading={statusSummary.isLoading}
        />

        {/* Content: Groups + Machines Table */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Capacity Groups Sidebar */}
          <div className="lg:col-span-1 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#45bdd6]/20 focus:border-[#45bdd6]"
              />
            </div>

            <button
              onClick={() => setSelectedGroupId(null)}
              className={clsx(
                'w-full text-left text-sm px-4 py-2.5 rounded-lg transition-colors',
                selectedGroupId === null
                  ? 'bg-[#45bdd6]/10 text-[#45bdd6] font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              All Machines
            </button>

            {capacityGroups.isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {groupsList.map((group) => (
                  <CapacityGroupCard
                    key={group.id}
                    group={group}
                    selected={selectedGroupId === group.id}
                    onClick={() => setSelectedGroupId(group.id)}
                  />
                ))}
                {groupsList.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No capacity groups found
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Machines Table */}
          <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">
                {selectedGroupId
                  ? `Machines in Group`
                  : 'All Machines'}
                <span className="text-sm font-normal text-gray-400 ml-2">
                  ({machinesList.length})
                </span>
              </h2>
            </div>

            {machines.isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : machinesList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Server className="w-10 h-10 mb-3" />
                <p className="text-sm">No machines found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50/50">
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">
                        Machine
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">
                        Description
                      </th>
                      <th className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase">
                        Work Center
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {machinesList.map((machine) => (
                      <MachineRow
                        key={machine.id}
                        machine={machine}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {(capacityGroups.isError || machines.isError) && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">
                Error loading machine data
              </p>
              <p className="text-xs text-red-600 mt-1">
                Ensure the backend is running and migrations have been applied.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
